import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import BildAuswahl from '../components/BildAuswahl';
import { hinweis } from '../components/Hinweise';

const TYP_BADGE = {
  formular:        { label: 'Fest', color: '#1565c0', bg: '#e3f2fd' },
  formular_custom: { label: 'Eigene', color: '#2e7d32', bg: '#e8f5e9' },
  angebote:        { label: 'Angebote', color: '#e65100', bg: '#fff3e0' },
};

const DEFAULT_ICONS = { warteliste: '📋', kontakt: '✉️', probetraining: '🏊', angebote: '🎯' };

export default function Kacheln() {
  const navigate = useNavigate();
  const [kacheln, setKacheln] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bildDialog, setBildDialog] = useState(null);   // Kachel, für die ein Bild gewählt wird
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/api/admin/kacheln');
      setKacheln(res.data);
    } catch {
      setError('Kacheln konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (k) => {
    try {
      await api.put(`/api/admin/kacheln/${k.id}`, { aktiv: !k.aktiv });
      await load();
    } catch { hinweis.fehler('Fehler beim Speichern'); }
  };

  const handleOrder = async (k, direction) => {
    const idx = kacheln.findIndex(x => x.id === k.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= kacheln.length) return;
    const other = kacheln[swapIdx];
    try {
      await Promise.all([
        api.put(`/api/admin/kacheln/${k.id}`,    { reihenfolge: other.reihenfolge }),
        api.put(`/api/admin/kacheln/${other.id}`, { reihenfolge: k.reihenfolge }),
      ]);
      await load();
    } catch { hinweis.fehler('Fehler beim Sortieren'); }
  };

  // Fehler bewusst nicht abfangen – BildAuswahl zeigt sie im Dialog an.
  const handleBildAuswahl = async (bildId) => {
    await api.put(`/api/admin/kacheln/${bildDialog.id}/bild`, { bild_id: bildId });
    setBildDialog(null);
    await load();
  };

  const handleDelete = async (k) => {
    if (!window.confirm(`Kachel "${k.titel}" wirklich löschen? Alle Einreichungen gehen verloren.`)) return;
    setDeleting(k.id);
    try {
      await api.delete(`/api/admin/kacheln/${k.id}`);
      await load();
    } catch (err) {
      hinweis.fehler(err.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Kacheln verwalten</h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/kacheln/neu')}>
          + Neue Kachel
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
        Die Kacheln erscheinen auf der Widget-Startseite. Über „🖼 Bild" lädst du ein neues Bild hoch
        (JPG / PNG / WebP bis 2 MB, wird automatisch auf höchstens 500 KB verkleinert) oder wählst ein
        vorhandenes aus der <Link to="/bilder">Bildbibliothek</Link>.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '1rem' }}>
          {kacheln.map((k, idx) => {
            const typInfo = TYP_BADGE[k.typ] || TYP_BADGE.formular;
            const feldCount = (k.formular_felder || []).filter(f => f.typ !== 'ueberschrift' && !f.ausgeblendet).length;

            return (
              <div key={k.id} className="card" style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>

                {/* Bild-Vorschau */}
                <div style={{ height: 160, background: '#f0f4f8', position: 'relative', flexShrink: 0 }}>
                  {k.bild_pfad ? (
                    <img src={`/uploads/${k.bild_pfad}?t=${k.updated_at}`} alt={k.titel}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #003366, #0099cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
                      {DEFAULT_ICONS[k.key] || '⭐'}
                    </div>
                  )}
                  <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 4 }}>
                    <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, background: typInfo.bg, color: typInfo.color }}>
                      {typInfo.label}
                    </span>
                    {feldCount > 0 && (
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, background: '#f3e5f5', color: '#6a1b9a' }}>
                        {feldCount} {feldCount === 1 ? 'Feld' : 'Felder'}
                      </span>
                    )}
                  </div>
                  <span style={{
                    position: 'absolute', top: 8, right: 8,
                    padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700,
                    background: k.aktiv ? '#e8f5e9' : '#f5f5f5',
                    color: k.aktiv ? '#2e7d32' : '#616161',
                    boxShadow: '0 1px 4px rgba(0,0,0,.15)',
                  }}>
                    {k.aktiv ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>

                {/* Info */}
                <div style={{ padding: '0.875rem 1rem 0.5rem', flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--primary)', marginBottom: '0.2rem' }}>
                    {k.titel}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', minHeight: '1.2em' }}>
                    {k.beschreibung || <em>Keine Beschreibung</em>}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
                    Route: <code style={{ background: '#f4f7fb', padding: '1px 4px', borderRadius: 3 }}>{k.route}</code>
                  </div>
                </div>

                {/* Aktionen */}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', borderTop: '1px solid var(--border)', padding: '0.75rem 1rem', alignItems: 'center' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => navigate(`/kacheln/${k.id}/bearbeiten`)}>
                    ✏ Bearbeiten
                  </button>

                  <button className="btn btn-secondary btn-sm" onClick={() => setBildDialog(k)}>
                    🖼 Bild
                  </button>

                  <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleOrder(k, 'up')} disabled={idx === 0} title="Nach oben">↑</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleOrder(k, 'down')} disabled={idx === kacheln.length - 1} title="Nach unten">↓</button>
                    <button className={`btn btn-sm ${k.aktiv ? 'btn-warning' : 'btn-success'}`} onClick={() => handleToggle(k)}>
                      {k.aktiv ? 'Aus' : 'Ein'}
                    </button>
                    {k.typ === 'formular_custom' && (
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(k)} disabled={deleting === k.id}
                        title="Kachel löschen">
                        {deleting === k.id ? '…' : '✕'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {bildDialog && (
        <BildAuswahl
          titel={`Bild für „${bildDialog.titel}"`}
          aktuellerPfad={bildDialog.bild_pfad}
          onAuswahl={handleBildAuswahl}
          onClose={() => setBildDialog(null)}
        />
      )}
    </div>
  );
}
